import React from 'react';
import { BezierCurve } from './bezier';
import { GridState, HoverElement } from '../types';
import { HourlyData, GridStateWithSolution } from '../powerFlow/multiTimestepOptimization';
import { getOrderedSourceNodes } from '../utils/sourceOrder';

interface RenderGridProps {
  timeToX: (time: number) => number;
  valueToY: (value: number) => number;
  actualWidth: number;
  actualHeight: number;
  margin: number;
  timeScale: number;
  valueScale: number;
  leftMargin?: number;
}

interface RenderAxesProps extends RenderGridProps {
  valueLabel: string;
  valueUnits?: string;
}

interface RenderControlPointsProps {
  controlPoints: BezierCurve;
  timeToX: (time: number) => number;
  valueToY: (value: number) => number;
  handleMouseDown: (e: React.MouseEvent, pointIndex: number, type: 'point' | 'cp1' | 'cp2') => void;
  showControlPoints?: boolean;
}

interface GenerateCurvePathProps {
  controlPoints: BezierCurve;
  timeToX: (time: number) => number;
  valueToY: (value: number) => number;
  timeScale?: number;
  extendToTimeScale?: boolean;
}

interface RenderCurrentTimeIndicatorProps {
  hoverHour: number | null | undefined;
  timeToX: (time: number) => number;
  margin: number;
  actualHeight: number;
}

interface RenderFilledAreaProps {
  controlPoints: BezierCurve;
  timeToX: (time: number) => number;
  valueToY: (value: number) => number;
  actualHeight: number;
  margin: number;
  fillColor: string;
  fillOpacity?: number;
  timeScale?: number;
  extendToTimeScale?: boolean;
  leftMargin?: number;
}

interface RenderStackedAreasProps {
  gridState?: GridState;
  hourlyData: HourlyData[];
  hourlyGridStates?: Array<{ hour: number; gridState: GridStateWithSolution }>;
  timeToX: (time: number) => number;
  loadToY: (load: number) => number;
  colorPalette: string[];
  hoverElement?: HoverElement | null;
  onElementHover?: (element: HoverElement | null) => void;
}

interface RenderInfeasibleRegionsProps {
  hourlyData: HourlyData[];
  timeToX: (time: number) => number;
  actualHeight: number;
  margin: number;
  leftMargin?: number;
}

export function renderGrid({
  timeToX,
  valueToY,
  actualWidth,
  actualHeight,
  margin,
  timeScale,
  valueScale,
  leftMargin,
}: RenderGridProps): React.ReactElement[] {
  const lines = [];

  // Vertical lines (time)
  for (let i = 0; i <= timeScale; i += timeScale / 6) {
    const x = timeToX(i);
    lines.push(
      <line
        key={`v-${i}`}
        x1={x}
        y1={margin}
        x2={x}
        y2={actualHeight - margin}
        stroke="#e0e0e0"
        strokeWidth={i % (timeScale / 2) === 0 ? 2 : 1}
      />
    );
  }

  // Horizontal lines (value)
  for (let i = 0; i <= valueScale; i += valueScale / 5) {
    const y = valueToY(i);
    lines.push(
      <line
        key={`h-${i}`}
        x1={leftMargin || margin}
        y1={y}
        x2={actualWidth - margin}
        y2={y}
        stroke="#e0e0e0"
        strokeWidth={1}
      />
    );
  }

  return lines;
}

export function renderAxes({
  timeToX,
  valueToY,
  actualWidth,
  actualHeight,
  margin,
  timeScale,
  valueScale,
  valueLabel,
  valueUnits = '%',
  leftMargin,
}: RenderAxesProps): React.ReactElement[] {
  const elements = [];

  // X-axis labels (time)
  for (let i = 0; i <= timeScale; i += timeScale / 4) {
    const x = timeToX(i);
    elements.push(
      <text
        key={`x-label-${i}`}
        x={x}
        y={actualHeight - 15}
        textAnchor="middle"
        fontSize="12"
        fill="#666"
      >
        {i}:00
      </text>
    );
  }

  // Y-axis labels
  for (let i = 0; i <= valueScale; i += valueScale / 5) {
    const y = valueToY(i);
    elements.push(
      <text
        key={`y-label-${i}`}
        x={(leftMargin || margin) - 5}
        y={y + 4}
        textAnchor="end"
        fontSize="12"
        fill="#666"
      >
        {Math.round(i)}{valueUnits}
      </text>
    );
  }

  // Axis titles
  elements.push(
    <text
      key="x-title"
      x={actualWidth / 2}
      y={actualHeight - 2}
      textAnchor="middle"
      fontSize="14"
      fill="#333"
      fontWeight="bold"
    >
      Time (Hours)
    </text>
  );

  elements.push(
    <text
      key="y-title"
      x={15}
      y={actualHeight / 2}
      textAnchor="middle"
      fontSize="14"
      fill="#333"
      fontWeight="bold"
      transform={`rotate(-90, 15, ${actualHeight / 2})`}
    >
      {valueLabel}
    </text>
  );

  return elements;
}

export function renderControlPoints({
  controlPoints,
  timeToX,
  valueToY,
  handleMouseDown,
  showControlPoints = false,
}: RenderControlPointsProps): React.ReactElement[] {
  return controlPoints.map((point, index) => {
    const x = timeToX(point.x);
    const y = valueToY(point.y);
    const cp1x = timeToX(point.cp1x);
    const cp1y = valueToY(point.cp1y);
    const cp2x = timeToX(point.cp2x);
    const cp2y = valueToY(point.cp2y);

    return (
      <g key={index}>
        {showControlPoints && (
          <>
            {/* Control point lines */}
            <line
              x1={x}
              y1={y}
              x2={cp1x}
              y2={cp1y}
              stroke="#999"
              strokeWidth={1}
              strokeDasharray="3,3"
            />
            <line
              x1={x}
              y1={y}
              x2={cp2x}
              y2={cp2y}
              stroke="#999"
              strokeWidth={1}
              strokeDasharray="3,3"
            />

            {/* Control points */}
            <circle
              cx={cp1x}
              cy={cp1y}
              r={4}
              fill="#ff6b6b"
              stroke="#fff"
              strokeWidth={2}
              style={{ cursor: "grab" }}
              onMouseDown={(e) => handleMouseDown(e, index, "cp1")}
            />
            <circle
              cx={cp2x}
              cy={cp2y}
              r={4}
              fill="#ff6b6b"
              stroke="#fff"
              strokeWidth={2}
              style={{ cursor: "grab" }}
              onMouseDown={(e) => handleMouseDown(e, index, "cp2")}
            />
          </>
        )}

        {/* Main point */}
        <circle
          cx={x}
          cy={y}
          r={6}
          fill="#4CAF50"
          stroke="#fff"
          strokeWidth={3}
          style={{ cursor: "grab" }}
          onMouseDown={(e) => handleMouseDown(e, index, "point")}
        />

        {/* Point label */}
        <text
          x={x}
          y={y - 15}
          textAnchor="middle"
          fontSize="11"
          fill="#333"
          fontWeight="bold"
        >
          {point.x.toFixed(0)}:00
        </text>
      </g>
    );
  });
}

export function generateCurvePath({
  controlPoints,
  timeToX,
  valueToY,
  timeScale = 24,
  extendToTimeScale = false,
}: GenerateCurvePathProps): string {
  if (controlPoints.length === 0) return "";

  // Sort control points by time to ensure proper order
  const sortedPoints = [...controlPoints].sort((a, b) => a.x - b.x);

  let path: string;

  if (extendToTimeScale) {
    // LoadCurve style - start from time 0 with the first point's value
    path = `M ${timeToX(0)} ${valueToY(sortedPoints[0].y)}`;
    
    // Create smooth curve through all control points
    for (let i = 0; i < sortedPoints.length - 1; i++) {
      const point = sortedPoints[i];
      const nextPoint = sortedPoints[i + 1];

      const x1 = timeToX(point.x);
      const y1 = valueToY(point.y);
      const cp1x = timeToX(point.cp2x);
      const cp1y = valueToY(point.cp2y);
      const cp2x = timeToX(nextPoint.cp1x);
      const cp2y = valueToY(nextPoint.cp1y);
      const x2 = timeToX(nextPoint.x);
      const y2 = valueToY(nextPoint.y);

      // Line to first point if needed
      if (i === 0 && point.x > 0) {
        path += ` L ${x1} ${y1}`;
      } else if (i === 0) {
        // If first point is at time 0, start directly from it
        path = `M ${x1} ${y1}`;
      }

      path += ` C ${cp1x} ${cp1y} ${cp2x} ${cp2y} ${x2} ${y2}`;
    }

    // Extend to end of time scale with the last point's value
    const lastPoint = sortedPoints[sortedPoints.length - 1];
    if (lastPoint.x < timeScale) {
      path += ` L ${timeToX(timeScale)} ${valueToY(lastPoint.y)}`;
    }
  } else {
    // WindCurve/SunCurve style - simple curve through points
    path = `M ${timeToX(sortedPoints[0].x)} ${valueToY(sortedPoints[0].y)}`;
    
    for (let i = 1; i < sortedPoints.length; i++) {
      const prev = sortedPoints[i - 1];
      const curr = sortedPoints[i];
      
      path += ` C ${timeToX(prev.cp2x)} ${valueToY(prev.cp2y)}, ${timeToX(curr.cp1x)} ${valueToY(curr.cp1y)}, ${timeToX(curr.x)} ${valueToY(curr.y)}`;
    }
  }
  
  return path;
}

export function renderCurrentTimeIndicator({
  hoverHour,
  timeToX,
  margin,
  actualHeight,
}: RenderCurrentTimeIndicatorProps): React.ReactElement | null {
  if (hoverHour === null || hoverHour === undefined) {
    return null;
  }

  return (
    <line
      x1={timeToX(hoverHour)}
      y1={margin}
      x2={timeToX(hoverHour)}
      y2={actualHeight - margin}
      stroke="#FF6B6B"
      strokeWidth={2}
      strokeDasharray="5,5"
      opacity={0.8}
      pointerEvents="none"
    />
  );
}

export function renderFilledArea({
  controlPoints,
  timeToX,
  valueToY,
  actualHeight,
  margin,
  fillColor,
  fillOpacity = 0.3,
  timeScale = 24,
  extendToTimeScale = false,
  leftMargin,
}: RenderFilledAreaProps): React.ReactElement | null {
  if (controlPoints.length === 0) return null;

  // Sort control points by time to ensure proper order
  const sortedPoints = [...controlPoints].sort((a, b) => a.x - b.x);

  let path: string;

  if (extendToTimeScale) {
    // Start from time 0 at the bottom
    path = `M ${timeToX(0)} ${actualHeight - margin}`;
    
    // Line up to the curve start value at time 0
    path += ` L ${timeToX(0)} ${valueToY(sortedPoints[0].y)}`;
    
    // Follow the curve path (similar to generateCurvePath logic)
    for (let i = 0; i < sortedPoints.length - 1; i++) {
      const point = sortedPoints[i];
      const nextPoint = sortedPoints[i + 1];

      // Line to first point if needed
      if (i === 0 && point.x > 0) {
        path += ` L ${timeToX(point.x)} ${valueToY(point.y)}`;
      }

      path += ` C ${timeToX(point.cp2x)} ${valueToY(point.cp2y)}, ${timeToX(nextPoint.cp1x)} ${valueToY(nextPoint.cp1y)}, ${timeToX(nextPoint.x)} ${valueToY(nextPoint.y)}`;
    }

    // Extend to end of time scale with the last point's value
    const lastPoint = sortedPoints[sortedPoints.length - 1];
    if (lastPoint.x < timeScale) {
      path += ` L ${timeToX(timeScale)} ${valueToY(lastPoint.y)}`;
    }
    
    // Line down to the bottom-right and close the path
    path += ` L ${timeToX(timeScale)} ${actualHeight - margin}`;
    path += ` Z`; // Close the path
  } else {
    // Original behavior - just follow the control points
    path = `M ${timeToX(sortedPoints[0].x)} ${actualHeight - margin}`;
    
    // Line up to the curve start point
    path += ` L ${timeToX(sortedPoints[0].x)} ${valueToY(sortedPoints[0].y)}`;
    
    // Follow the curve path
    for (let i = 1; i < sortedPoints.length; i++) {
      const prev = sortedPoints[i - 1];
      const curr = sortedPoints[i];
      
      path += ` C ${timeToX(prev.cp2x)} ${valueToY(prev.cp2y)}, ${timeToX(curr.cp1x)} ${valueToY(curr.cp1y)}, ${timeToX(curr.x)} ${valueToY(curr.y)}`;
    }
    
    // Line down to the bottom-right and close the path
    const lastPoint = sortedPoints[sortedPoints.length - 1];
    path += ` L ${timeToX(lastPoint.x)} ${actualHeight - margin}`;
    path += ` Z`; // Close the path
  }

  return (
    <path
      d={path}
      fill={fillColor}
      fillOpacity={fillOpacity}
      stroke="none"
    />
  );
}

export function renderStackedAreas({
  gridState,
  hourlyData,
  hourlyGridStates,
  timeToX,
  loadToY,
  colorPalette,
  hoverElement,
  onElementHover,
}: RenderStackedAreasProps): React.ReactElement[] | null {
  if (!gridState || hourlyData.length === 0) return null;

  // Order sources cheapest-first so the stack shows base load (cheapest) on the
  // bottom and peaker plants on top. Shared with the legend so colors match.
  const sourceNodes = getOrderedSourceNodes(gridState);

  if (sourceNodes.length === 0) return null;

  const areas: JSX.Element[] = [];

  // For each source node (generator or battery), create a stacked area
  sourceNodes.forEach((sourceNode, nodeIndex) => {
    const points: string[] = [];
    
    // Calculate cumulative values for stacking
    for (let hour = 0; hour < 24; hour++) {
      const data = hourlyData[hour];
      if (!data || data.type === 'infeasible') continue;

      // Calculate cumulative value from all previous nodes
      let cumulativeBelow = 0;
      
      // Add generators that come before this node
      for (let i = 0; i < nodeIndex; i++) {
        const prevNode = sourceNodes[i];
        if (prevNode.type === 'generator') {
          // Find this generator in the hourly data
          const genData = data.generators.find(gen => gen.id === prevNode.id);
          cumulativeBelow += genData?.value || 0;
        } else if (prevNode.type === 'battery') {
          // For batteries, get discharge value from LP solution
          const dischargeVar = `P_discharge_${prevNode.id}_t${hour}`;
          const hourlyGridState = hourlyGridStates?.find(h => h.hour === hour);
          const lpSolution = hourlyGridState?.gridState?.lpSolutions?.get(0);
          const dischargeValue = lpSolution?.Columns?.[dischargeVar]?.Primal || 0;
          cumulativeBelow += dischargeValue;
        }
      }
      
      // Add this node's contribution
      let nodeValue = 0;
      if (sourceNode.type === 'generator') {
        const genData = data.generators.find(gen => gen.id === sourceNode.id);
        nodeValue = genData?.value || 0;
      } else if (sourceNode.type === 'battery') {
        // For batteries, get discharge value from LP solution
        const dischargeVar = `P_discharge_${sourceNode.id}_t${hour}`;
        const hourlyGridState = hourlyGridStates?.find(h => h.hour === hour);
        const lpSolution = hourlyGridState?.gridState?.lpSolutions?.get(0);
        nodeValue = lpSolution?.Columns?.[dischargeVar]?.Primal || 0;
      }
      
      const cumulativeIncluding = cumulativeBelow + nodeValue;

      const x = timeToX(hour);
      const yBottom = loadToY(cumulativeBelow);
      const yTop = loadToY(cumulativeIncluding);

      if (hour === 0) {
        // Start at bottom
        points.push(`M ${x} ${yBottom}`);
        points.push(`L ${x} ${yTop}`);
      } else {
        // Line to top
        points.push(`L ${x} ${yTop}`);
      }
    }

    // Close the path by going back along the bottom
    for (let hour = 23; hour >= 0; hour--) {
      const data = hourlyData[hour];
      if (!data || data.type === 'infeasible') continue;

      // Calculate cumulative value from all previous nodes (same logic as above)
      let cumulativeBelow = 0;
      for (let i = 0; i < nodeIndex; i++) {
        const prevNode = sourceNodes[i];
        if (prevNode.type === 'generator') {
          const genData = data.generators.find(gen => gen.id === prevNode.id);
          cumulativeBelow += genData?.value || 0;
        } else if (prevNode.type === 'battery') {
          // For batteries, get discharge value from LP solution
          const dischargeVar = `P_discharge_${prevNode.id}_t${hour}`;
          const hourlyGridState = hourlyGridStates?.find(h => h.hour === hour);
          const lpSolution = hourlyGridState?.gridState?.lpSolutions?.get(0);
          const dischargeValue = lpSolution?.Columns?.[dischargeVar]?.Primal || 0;
          cumulativeBelow += dischargeValue;
        }
      }

      const x = timeToX(hour);
      const yBottom = loadToY(cumulativeBelow);
      points.push(`L ${x} ${yBottom}`);
    }

    points.push('Z'); // Close path

    const isHighlighted = hoverElement?.type === 'Node' && hoverElement?.id === sourceNode.id;
    
    areas.push(
      <path
        key={`area-${sourceNode.id}`}
        d={points.join(' ')}
        fill={isHighlighted ? '#FFD700' : colorPalette[nodeIndex % colorPalette.length]}
        fillOpacity={isHighlighted ? 0.9 : 0.7}
        stroke={isHighlighted ? '#FFD700' : colorPalette[nodeIndex % colorPalette.length]}
        strokeWidth={isHighlighted ? 2 : 1}
        onMouseEnter={() => onElementHover?.({ type: 'Node', id: sourceNode.id })}
        onMouseLeave={() => onElementHover?.(null)}
        style={{ cursor: 'pointer' }}
      />
    );
  });

  return areas;
}

export function renderInfeasibleRegions({
  hourlyData,
  timeToX,
  actualHeight,
  margin,
  leftMargin,
}: RenderInfeasibleRegionsProps): React.ReactElement[] {
  const regions: JSX.Element[] = [];
  
  for (let hour = 0; hour < 24; hour++) {
    const data = hourlyData[hour];
    if (data && data.type === 'infeasible') {
      const x1 = timeToX(hour);
      const x2 = timeToX(hour + 1);
      
      regions.push(
        <rect
          key={`infeasible-${hour}`}
          x={x1}
          y={margin}
          width={x2 - x1}
          height={actualHeight - 2 * margin}
          fill="#ffb3d1"
          fillOpacity={0.6}
        />
      );
    }
  }
  
  return regions;
}
